import { client } from './backend/client'
import { reauthenticate } from './signin'
import store from './store'

// The fields a refusal named, each with the messages the server wrote for it.
//
// A list because a changeset can put several on one field -- an email that is
// both malformed and already taken is the obvious one -- and a field-keyed map
// because that is the shape the server sends and the one the form can render
// against without knowing which fields exist. Nothing is invented here: a key is
// only ever one the server named, so the screen cannot show an error against a
// field it does not have.
export type FieldErrors = { [field: string]: string[] }

// A refusal, from either of the two routes this module calls.
//
// `fields` is what to show against each input, and `message` is the one thing to
// say when there is nothing to show against one -- a server that could not be
// reached, or answered with something other than a refusal. Both on one error
// rather than two paths out of `saveProfile` and `changePassword`, because the
// two are the same outcome told at two levels of specificity, and a caller that
// has to ask which kind it caught can get it wrong in the direction that matters:
// reporting a whole-save failure as a field error, or -- the one this module
// exists to make impossible -- dropping a field error on the floor.
export class ProfileRefused extends Error {
  readonly fields: FieldErrors

  constructor (fields: FieldErrors, message: string) {
    super(message)
    this.name = 'ProfileRefused'
    this.fields = fields
  }
}

// Reads the field errors out of a refusal body.
//
// The shape is `Metalist.HTTP.Validation`'s and nothing else: `400` answers with
// `{"errors": {"<field>": ["<message>"]}}`. A body that is not that shape is read
// as no field errors at all rather than guessed at, which leaves the caller
// showing its own whole-form message -- the right answer for a `500`, for a proxy
// that answered with an HTML error page, and for a response shape this client has
// not been taught. Guessing would be worse in both directions: a body read as
// empty when it was not loses a message the server took the trouble to write, and
// a body read as fields when it was not puts an error against a field nobody can
// act on.
//
// A field whose value is not a list of strings is dropped for the same reason.
// `Validation` always sends one, so this cannot cost a message in practice; it is
// there so that a changed shape renders as an absent error the caller can still
// act on, rather than as a string the view would print inside a list.
// Exported, and only that: `adminusers.ts` reads the same body shape for the same
// reason and would otherwise carry a second copy of these rules. The rules are
// about `Metalist.HTTP.Validation` and not about the profile screen, which is why
// they can be shared; what is *not* shared is the refusal class, because a class
// called `ProfileRefused` thrown by the admin screen would tell a reader of a stack
// trace something untrue.
export function fieldErrorsFrom (body: unknown): FieldErrors {
  if (body === null || typeof body !== 'object') {
    return {}
  }

  const errors = (body as { errors?: unknown }).errors

  if (errors === null || typeof errors !== 'object') {
    return {}
  }

  const named = errors as { [field: string]: unknown }
  const collected: FieldErrors = {}

  Object.keys(named).forEach(field => {
    const messages = named[field]

    if (Array.isArray(messages) && messages.every(message => typeof message === 'string')) {
      collected[field] = messages
    }
  })

  return collected
}

// What `GET /api/users/profile` records about the caller, in the names this
// codebase spells them with.
//
// More than the session keeps, and deliberately so: the profile read exists to
// show what the server has on file, which includes the two timestamps and the
// active flag. A session is a decision about *authentication* -- enough for a
// user menu and for whether to offer administrative affordances -- and keeping
// the timestamps in it would put a second, drifting copy of the account in the
// store. The read is not stored at all: the screen holds it for as long as it is
// on screen, and the store is corrected only when the server says it changed.
//
// `is_admin` arrives as `is_admin` and is renamed on the way in, which is what
// `SessionUser` does with it and why the field is spelled this way here.
export interface Profile {
  id: string
  username: string
  name: string
  email: string
  active: boolean
  isAdmin: boolean
  insertedAt: string
  updatedAt: string
}

// The account as the profile route renders it. `Metalist.HTTP.View.user/1` is an
// allow list of eight fields, and this is what it allows -- named here field by
// field rather than spread, for the reason the view is an allow list on the
// server too: a column, or a virtual field, added to `users` later is not a field
// this screen is entitled to render until somebody puts it here on purpose.
//
// Both of the credential keys are absent because the server already refuses to
// send them; nothing here is the thing keeping them out. What the allow list is
// for is the field nobody expects -- the next one somebody adds -- which would
// otherwise arrive with nowhere to be looked over.
interface ProfileResponse {
  id: string
  username: string
  name: string
  email: string
  active: boolean
  // eslint-disable-next-line camelcase
  is_admin: boolean
  // eslint-disable-next-line camelcase
  inserted_at: string
  // eslint-disable-next-line camelcase
  updated_at: string
}

function profileFrom (response: ProfileResponse): Profile {
  return {
    id: response.id,
    username: response.username,
    name: response.name,
    email: response.email,
    active: response.active,
    isAdmin: response.is_admin,
    insertedAt: response.inserted_at,
    updatedAt: response.updated_at
  }
}

// Reads the caller's own record. The route takes no id -- there is no segment to
// take one from -- so this cannot be pointed at somebody else's account by
// accident, which is a property of the server's route rather than a check made
// here.
export async function loadProfile (): Promise<Profile> {
  const response = await client.get('/users/profile')

  return profileFrom(response.data)
}

// What a save carries: the two fields the form can change, and the username the
// record on screen already has.
//
// `:username` is required here rather than optional, which is the mechanism for
// the two decisions above being one. A caller cannot describe a save without
// saying which username it thinks it is saving against, so "send only what the
// form has" -- which is what a form without a username field would naturally do
// -- is not a mistake available to make. The alternative, an optional field that
// is left out, is the same code with one more branch and one more way for the
// server's refusal never to be asked for.
export interface ProfileFields {
  name: string
  email: string
  username: string
}

// Saves the name and the email, and answers what the account now records.
//
// On a refusal this throws rather than resolving with something to render: the
// two outcomes -- an account that changed and one that did not -- look identical
// from here otherwise, and a caller that forgot to check would report a change
// that had not happened. That matters most for the username, whose whole
// protection is that the refusal is loud.
export async function saveProfile (fields: ProfileFields): Promise<Profile> {
  let response

  try {
    response = await client.put('/users/profile', {
      name: fields.name,
      email: fields.email,
      username: fields.username
    })
  } catch (error) {
    // A `401` is not here. The response interceptor has already dropped the
    // session and sent the visitor to the sign-in form by the time this sees it,
    // and the screen is being left. Reading one as a whole-form failure would put
    // a second, contradicting message on a page nobody is on.
    //
    // One message for every refusal, whatever its status, because this is only
    // ever shown when the server named no field at all. There is nothing to
    // distinguish a `400` with an empty error map from a `500` that the user can
    // act on, and a message that told them which would be describing our plumbing
    // rather than their problem.
    if ((error as any).response !== undefined) {
      throw new ProfileRefused(
        fieldErrorsFrom((error as any).response.data),
        'Saving your changes did not work. Please try again.'
      )
    }

    // Anything that never reached the server -- no network, a request the browser
    // refused to send -- is left to propagate as itself. Reporting it as a refusal
    // would be a claim that the server turned the save down, which nobody knows.
    throw error
  }

  const updated = profileFrom(response.data)

  // The session's copy of the account, so that a user menu -- or anything else
  // reading the store -- shows the name that was just saved rather than the one
  // the user replaced. `userUpdated` rather than `sessionStarted`, because the
  // token is still good: a name is not a security event and the server revokes
  // nothing for this change, so making the user sign in again over a typo fix
  // would be a cost for nothing.
  store.commit('userUpdated', { name: updated.name, email: updated.email })

  return updated
}

// The two bounds on a new password, as `Metalist.Accounts.User` states them:
// `@min_password_length 8` and `@max_password_length 128`.
//
// Spelled out here rather than shared with the server, because there is nothing
// to share them *with* -- this is TypeScript and that is Elixir, and the only
// mechanism that would keep the two in step is a client that reads them from an
// endpoint that serves them. Which is why the numbers are asserted in this
// module's own spec as literals: a client whose copy drifted should fail a test,
// and the server stays the authority on whether the password is acceptable.
export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 128

// The rules the server enforces on the new password, said as field errors before
// the change is attempted.
//
// Only the length. The password route's other rules are that the two fields must
// be present and that the current one must verify, and none of those can be known
// before asking -- the second one is the server's alone to answer. A blank
// `current_password` is the one that could be caught here too, and deliberately
// is not: it is a required field rather than a rule about the value, every form
// already treats it that way, and duplicating it buys a saved round trip at the
// cost of a second place for the required set to be wrong in.
//
// Neither is the email format or the name length from `profile_changeset/2`,
// for the same reason, and with one more reason: a password is the one field
// where nothing about what has been typed is visible, so its rule cannot be read
// off the field itself. A name or an address can be checked by looking.
export function passwordFieldErrors (newPassword: string): FieldErrors {
  if (newPassword.length < PASSWORD_MIN_LENGTH) {
    return { password: [`Use at least ${PASSWORD_MIN_LENGTH} characters.`] }
  }

  if (newPassword.length > PASSWORD_MAX_LENGTH) {
    return { password: [`Use at most ${PASSWORD_MAX_LENGTH} characters.`] }
  }

  return {}
}

// What the two password fields hold. Typed as an interface rather than as two
// loose strings because that is how the form holds them, and a signature taking
// `(current, next, changed)` puts the one that must not be transposed in the
// middle.
export interface PasswordFields {
  currentPassword: string
  newPassword: string
}

// What the password form should hold once an attempt has finished, which is the
// decision about the two plaintexts the form is holding. The screen asks and
// applies the answer; nothing here reaches into the view.
//
// The current password is dropped on every outcome. After a change it has been
// used and is somebody else's business; after a refusal the server has judged it,
// so it is a value known to be wrong, and keeping it in the field helps nobody --
// which also means a wrong current password cannot be submitted twice by someone
// who was not paying attention. There is no second copy of it worth holding, and a
// populated password field is the one place on this screen where a credential
// would be rendered.
//
// The new password is kept after a refusal and dropped after a change. Kept,
// because the failure was usually about it -- the bounds, above all -- and
// retyping a hundred and twenty-eight characters to shorten it is the form
// defending itself against a mistake that costs the user nothing. Dropped once it
// *is* the account's credential, since a form left holding it is a form left
// behind on a screen.
//
// `changed` rather than a refusal or an error, because a screen calling this has
// two outcomes to hand over and a parameter that named them would put the
// difference between "refused" and "could not be reached" into the signature, for
// a decision that does not care which it was.
export function passwordFormAfter (attempted: PasswordFields, changed: boolean): PasswordFields {
  if (changed) {
    return { currentPassword: '', newPassword: '' }
  }

  return { currentPassword: '', newPassword: attempted.newPassword }
}

// The one thing to say when the password has changed but the client could not be
// signed back in. Separate from the refusal message above because this is not a
// refusal of anything: the password *has* changed, and saying "that did not work"
// would have the user try again -- a retry that the server would then refuse,
// because the current password they would offer is the one that no longer exists.
//
// Says to sign in with the new password rather than the old, because that is the
// difference between a user who is one form away from being signed in and one
// staring at a credential that no longer works.
const SIGNED_OUT_AFTER_CHANGE = 'Your password was changed, so sign in again with your new password.'

// Changes the password, proving the one the account has now.
//
// The proof goes in the body rather than in a header because that is the design
// of the route: `current_password` is the one thing a caller has to produce, and
// a request that cannot produce it has nothing to say about what it wants to
// change to. So it is sent here, and nothing else is: no identifier, because the
// route takes the account the auth plug resolved and has no id to point at
// another one.
export async function changePassword (currentPassword: string, newPassword: string): Promise<void> {
  const tooShort = passwordFieldErrors(newPassword)

  // Refused here rather than sent, so the answer is a field error against the
  // field and no request is made. Not a `throw` of something else: the screen
  // renders field errors and one whole-form message, and a length failure is the
  // former.
  if (Object.keys(tooShort).length > 0) {
    throw new ProfileRefused(tooShort, 'Changing your password did not work. Please try again.')
  }

  // The response is not read. It is the account as `View.user/1` renders it, and
  // the only thing this screen would do with it is put it somewhere durable --
  // which is where a plaintext would end up. What it is used for is that there
  // was one: `Accounts.change_password/2` has moved the account's session marker
  // and answered, so the credential below is the live one whether or not anybody
  // looks at this body.
  try {
    await client.put('/users/password', {
      current_password: currentPassword,
      password: newPassword
    })
  } catch (error) {
    if ((error as any).response !== undefined) {
      throw new ProfileRefused(
        fieldErrorsFrom((error as any).response.data),
        'Changing your password did not work. Please try again.'
      )
    }

    // Never reached the server, so the credential is untouched and the session
    // still good. Left to propagate as itself for the reason the save above does
    // the same: nothing refused anything.
    throw error
  }

  // A refusal must leave the session alone, and it does: revocation is the
  // *success* path's consequence, so the token the change was refused with is
  // still good and this function has not touched it. That is the backend's
  // property rather than this module's, but it is the reason there is nothing to
  // undo here on the way out of the `catch` -- and the reason a screen that
  // clears its password fields after a refusal is not throwing away a session.

  // Read before the session is dropped below, and only because it is needed to
  // sign back in: the username is the account's immutable identifier, so it is
  // still the right one after a change that can only have altered the credential.
  const user = store.state.user

  // The change succeeded, which means the token that made it is already revoked --
  // `Accounts.change_password/2` moves the marker before the route writes its
  // response, and the route deliberately says nothing about that. Dropping the
  // session here rather than after the sign-in below is the deliberate part: it
  // would otherwise sit in the store, and be sent on anything else that asked,
  // while a credential that authenticates nothing.
  store.commit('sessionCleared')

  // A token with no account behind it cannot be signed back in: there is no
  // username to name, and reading one out of the token is exactly what a client
  // must not do. The change has already been made by this point, deliberately --
  // the request authenticated, and refusing it here would leave a user with a
  // broken session and no way to change their password from the screen at all.
  // They still know the password they just chose, so they can get back in.
  if (user === null) {
    throw new ProfileRefused({}, SIGNED_OUT_AFTER_CHANGE)
  }

  try {
    // By username rather than by email, and the reason is the same one: the
    // username cannot change, the email can, and an identifier that survives a
    // credential change is the one to reach for. `signInBody` will send it as a
    // username because it carries no `@`.
    await reauthenticate(user.username, newPassword)
  } catch (error) {
    // Whatever went wrong -- the credentials were refused, the network is not
    // there, the server answered with a `500` -- the outcome for the user is the
    // same one: the password has changed and this session is finished. One message
    // for all of them, because telling them which would not help and would be
    // guesswork about our own plumbing.
    //
    // The session is already cleared above, so there is nothing to undo and the
    // `401` interceptor is not relied on for it -- that interceptor only acts on
    // a `401`, and a `500` here would otherwise leave a revoked token in place.
    throw new ProfileRefused({}, SIGNED_OUT_AFTER_CHANGE)
  }
}
